// File view (/v/:vaultId/*path): LIVE-subscribes exactly one content doc,
// renders live-updating markdown, and inline/download attachment views. The
// file name is the breadcrumb (shell header) and presence + history are header
// actions (VaultPage), so this just renders the content. Canvas files are
// listed but shown as "open in Obsidian" (web-viewer spec §Explicit non-goals).

import { useEffect, useState } from "react";
import { IconAlertTriangle, IconDownload } from "@tabler/icons-react";
import { contentText, type FileEntry } from "@cairn/core";
import { attachmentUrl } from "../api";
import { displayName, fileName, isImagePath } from "../wikiLinks";
import { useApi } from "../session";
import { useDelayedLoading } from "../lib/use-delayed-loading";
import type { ViewerSync } from "../viewerSync";
import { Markdown } from "./Markdown";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

export interface FileViewProps {
  sync: ViewerSync;
  vaultId: string;
  path: string;
  entries: FileEntry[];
}

function isCanvas(path: string): boolean {
  return path.toLowerCase().endsWith(".canvas");
}

function OpeningState({ label }: { label: string }) {
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Spinner className="size-5" />
        </EmptyMedia>
        <EmptyTitle className="font-mono text-xs font-normal tracking-wider text-muted-foreground uppercase">
          {label}
        </EmptyTitle>
      </EmptyHeader>
    </Empty>
  );
}

/** Markdown note: subscribe LIVE, render, live-update as edits arrive. */
function NoteView({ sync, vaultId, entry, entries }: {
  sync: ViewerSync;
  vaultId: string;
  entry: FileEntry;
  entries: FileEntry[];
}) {
  const api = useApi();
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const showOpening = useDelayedLoading(text === null);
  const docId = entry.fileId;

  useEffect(() => {
    let cancelled = false;
    const readText = (): void => {
      const doc = sync.docs.getDoc(docId);
      if (doc && !cancelled) setText(contentText(doc).toString());
    };
    const offChange = sync.onDocChange(docId, readText);
    sync
      .openFile(docId)
      .then(readText)
      .catch((e: unknown) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
      offChange();
      void sync.closeFile(docId);
    };
  }, [sync, docId]);

  // Steady-state incremental updates mutate the Y.Text without a resync;
  // observe the text itself for character-level liveness.
  useEffect(() => {
    const doc = sync.docs.getDoc(docId);
    if (!doc || text === null) return;
    const t = contentText(doc);
    const observer = (): void => setText(t.toString());
    t.observe(observer);
    return () => t.unobserve(observer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sync, docId, text === null]);

  if (error) return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;
  // Delay the opening placeholder so a quick doc read never flashes it.
  if (text === null) return showOpening ? <OpeningState label="Opening file…" /> : null;
  // Obsidian shows the file name (minus a trailing `.md`) as a title atop the
  // note, in addition to any H1 in the body — so we render both.
  const title = displayName(fileName(entry.path));
  return (
    <div className="min-w-0 max-w-3xl flex-1">
      <h1 className="note-title mb-6 border-b pb-2 text-3xl font-bold tracking-tight text-balance">
        {title}
      </h1>
      <Markdown text={text} vaultId={vaultId} entries={entries} api={api} sourcePath={entry.path} />
    </div>
  );
}

/** Attachment: image inline via presigned GET, anything else a download link. */
function AttachmentView({ vaultId, entry }: { vaultId: string; entry: FileEntry }) {
  const api = useApi();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hash = entry.hash;

  useEffect(() => {
    if (hash === undefined) return;
    let cancelled = false;
    attachmentUrl(api, vaultId, hash)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [api, vaultId, hash]);

  if (hash === undefined || error) {
    return (
      <Empty className="border" role="alert" aria-live="assertive">
        <EmptyHeader>
          <EmptyMedia variant="icon" className="bg-destructive/10 text-destructive">
            <IconAlertTriangle />
          </EmptyMedia>
          <EmptyTitle>Attachment unavailable</EmptyTitle>
          {error ? <EmptyDescription>{error}</EmptyDescription> : null}
        </EmptyHeader>
      </Empty>
    );
  }
  if (!url) return <OpeningState label="Fetching attachment…" />;
  if (isImagePath(entry.path)) {
    return <img src={url} alt={entry.path} className="max-w-full rounded-lg" />;
  }
  const name = displayName(fileName(entry.path));
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <IconDownload />
        </EmptyMedia>
        <EmptyTitle>{name}</EmptyTitle>
        <EmptyDescription>
          This file type isn't shown in the browser — download it to open it.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button variant="outline" render={<a href={url} download />}>
          <IconDownload data-icon="inline-start" />
          Download
        </Button>
      </EmptyContent>
    </Empty>
  );
}

export function FileView({ sync, vaultId, path, entries }: FileViewProps) {
  const entry = entries.find((e) => e.path === path);
  if (!entry)
    return (
      <Empty className="border">
        <EmptyHeader>
          <EmptyTitle>File not found</EmptyTitle>
          <EmptyDescription>Nothing lives at “{path}” in this vault.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  if (isCanvas(entry.path)) {
    return (
      <Alert>
        <AlertDescription>
          Canvas files aren't rendered here — open this one in Obsidian.
        </AlertDescription>
      </Alert>
    );
  }
  if (entry.kind === "md") {
    return <NoteView sync={sync} vaultId={vaultId} entry={entry} entries={entries} />;
  }
  return <AttachmentView vaultId={vaultId} entry={entry} />;
}
